import classNames from 'classnames';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { NonRelevantCell } from 'components/common-form/builder/form/node/field/NonRelevantField';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { IntlKey } from 'locales';
import { DealSplitData } from 'models/forms/DealSplitData';
import { DealSplitModalFieldMetadata } from 'models/forms/fields/DealSplitModalFieldMetadata';
import { FC, useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { ProductNode } from 'state/product-description/model';
import styled from 'styled-components';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';

export const DealSplitModalFieldLabel = styled.div`
  cursor: pointer;

  &:hover {
    background-color: var(--bs-bg-lvl2);
  }
`;

export const DealSplitModal = styled(Modal)`
  @media (min-width: 1440px) {
    --bs-modal-width: 1300px;
  }

  .modal-content,
  .modal-body {
    overflow: visible;
  }
`;

export const BatchDealSplitModalField: FC<ProductNodeBuilderProps> = ({
  tabId,
  node,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  batchColumnIndex = 0,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { modalFieldId, type } = useFieldMetadata<DealSplitModalFieldMetadata>(node.id)!;
  const patchedFieldClassName = usePatchedFieldHighlight(node.id);
  const [dealSplitData] = useData<DealSplitData[]>(modalFieldId);

  const dealSplitNode = useMemo<ProductNode | undefined>(
    () => node.children?.find(n => n.id === modalFieldId),
    [node, modalFieldId],
  );

  const nbSelectedCustodian = useMemo(() => {
    return dealSplitData?.filter(item => item.Custodian).length ?? 0;
  }, [dealSplitData]);

  const toggle = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  if (type === 'Blank') {
    return <NonRelevantCell />;
  }

  return (
    <>
      <BlockOrientationFormGroup inputId={dealSplitNode?.id ?? ''} className={patchedFieldClassName}>
        <DealSplitModalFieldLabel
          className={classNames(
            'form-control',
            'd-flex flex-row align-items-center justify-content-between h-100 pe-0',
          )}
          onClick={toggle}
        >
          <div className="user-select-none">
            <FormattedMessage id={`form.${modalFieldId}.custodians` as IntlKey} values={{ nb: nbSelectedCustodian }} />
          </div>
          <div className="ms-auto">
            <button className={classNames('btn btn-icon', 'btn-flat-secondary')} onClick={toggle}>
              <i className="icon icon-sum">account_balance</i>
            </button>
          </div>
        </DealSplitModalFieldLabel>
      </BlockOrientationFormGroup>
      <DealSplitModal backdrop="static" data-e2e="modal" show={isModalOpen} size="xl" keyboard={false} onHide={toggle}>
        <ModalHeader className="pb-2" closeButton>
          <ModalTitle as="h3">
            <span className="d-flex align-items-center">
              <FormattedMessage
                id={`form.${modalFieldId}.title` as IntlKey}
                values={{ quoteIndex: batchColumnIndex + 1 }}
              />
            </span>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {dealSplitNode && (
            <>
              <RenderFormNodeComponent
                key={dealSplitNode.id}
                tabId={tabId}
                level={1}
                workspaceType="newBatch"
                quoteWorkspaceVersion={quoteWorkspaceVersion}
                node={dealSplitNode}
                mustForceAdvancedMode={mustForceAdvancedMode}
                batchWorkspaceVersion={batchWorkspaceVersion}
              />
            </>
          )}
        </ModalBody>
      </DealSplitModal>
    </>
  );
};

BatchDealSplitModalField.displayName = 'BatchDealSplitModalField';
